'use client';

import type { CSSProperties } from 'react';
import Image from 'next/image';
import { animate, motion, useMotionValue, useReducedMotion, useTransform } from 'motion/react';
import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { gsap } from '../lib/gsap';
import { HeroTokenCardFace } from './hero-token-card';

/**
 * Token art (WebP) for the hero curve, left → right. Served from `public/images/`.
 */
export const HERO_BOX_IMAGES: readonly string[] = [
  '/images/image1.webp',
  '/images/image2.webp',
  '/images/image3.webp',
  '/images/image4.webp',
  '/images/image5.webp',
  '/images/image6.webp',
  '/images/image7.webp',
] as const;

const N = 7;
// Desktop hero humans sizing is controlled by responsive Tailwind classes below.
// Keep intrinsic size stable to avoid unintended desktop changes.
const HUMAN_W = 400;
const HUMAN_H = 400;

/** Fan rotation (°): center upright; ±4° steps outward left/right. */
export function heroTileRotationDeg(index: number): number {
  return (index - (N - 1) / 2) * 4;
}

type BoxDim = { w: number; h: number; gap: number };

type VisualConfig = { scale: number; box: BoxDim };

/** Hero token cards: 300×300 below `sm`, 400×400 from `sm` up (matches Tailwind 640px). */
const HERO_TILE_SM_UP = 400;
const HERO_TILE_MOBILE = 300;
/** Layout spacing constant for arc math (tighter row). */
const HERO_TILE_GAP = 8;

const HERO_SM_MEDIA_QUERY = '(min-width: 640px)';

function useMatchesSmBreakpoint(): boolean {
  return useSyncExternalStore(
    (onStoreChange) => {
      const mq = window.matchMedia(HERO_SM_MEDIA_QUERY);
      mq.addEventListener('change', onStoreChange);
      return () => mq.removeEventListener('change', onStoreChange);
    },
    () => window.matchMedia(HERO_SM_MEDIA_QUERY).matches,
    () => false
  );
}

function useHeroVisualConfig(): VisualConfig {
  const isSmUp = useMatchesSmBreakpoint();
  const tile = isSmUp ? HERO_TILE_SM_UP : HERO_TILE_MOBILE;
  // Stable object identity across renders where `tile` hasn't changed, so consumers that memoize on
  // `box` itself (rather than its fields) don't invalidate every render.
  const box = useMemo<BoxDim>(() => ({ w: tile, h: tile, gap: HERO_TILE_GAP }), [tile]);
  return useMemo(() => ({ scale: 1, box }), [box]);
}

type Stage = 0 | 1 | 2 | 3;

const HERO_SPEED_MULT = 1 / 1.15; // 15% faster

/** Stack rise — spring on x / y / rotate only (opacity snaps — no fade). */
export const HERO_CARD_SPRING_STACK = {
  type: 'spring' as const,
  stiffness: 130,
  damping: 30,
  mass: 0.46,
};
export const HERO_CARD_STAGGER_STACK_SEC = 0.042 * HERO_SPEED_MULT;

/**
 * Arc spread — softer, higher damping so motion glides; separate from stack so it doesn’t feel stiff.
 */
export const HERO_CARD_SPRING_SPREAD = {
  type: 'spring' as const,
  stiffness: 82,
  damping: 38,
  mass: 0.56,
};
export const HERO_CARD_STAGGER_SPREAD_SEC = 0.034 * HERO_SPEED_MULT;

/** Humans: fade in at final position (no vertical rise). */
export const HERO_HUMAN_FADE_IN = {
  duration: 0.38 * HERO_SPEED_MULT,
  ease: [0.22, 1, 0.36, 1] as const,
};

/** Center strip horizontally; do not use scaleX — it squashes width only and 400×400 tiles read as tall rectangles. */
function heroTileStripInnerStyle(
  spanX: number,
  height: number
): CSSProperties {
  return {
    width: spanX,
    height,
    left: '50%',
    bottom: 0,
    transform: 'translateX(-50%)',
    transformOrigin: 'center bottom',
  };
}

const ELLIPSE_T0 = Math.PI;
const ELLIPSE_T1 = 2 * Math.PI;

function ellipseArcSpeed(a: number, b: number, t: number): number {
  const st = Math.sin(t);
  const ct = Math.cos(t);
  return Math.sqrt(a * a * st * st + b * b * ct * ct);
}

function arcLengthOnEllipse(
  a: number,
  b: number,
  tStart: number,
  tEnd: number,
  steps = 128
): number {
  if (tEnd <= tStart) return 0;
  const dt = (tEnd - tStart) / steps;
  let s = 0;
  for (let k = 0; k < steps; k++) {
    const t = tStart + (k + 0.5) * dt;
    s += ellipseArcSpeed(a, b, t) * dt;
  }
  return s;
}

function tForAccumulatedArcLength(
  a: number,
  b: number,
  targetFromT0: number,
  totalLen: number
): number {
  if (targetFromT0 <= 0) return ELLIPSE_T0;
  if (targetFromT0 >= totalLen - 1e-6) return ELLIPSE_T1;
  let lo = ELLIPSE_T0;
  let hi = ELLIPSE_T1;
  for (let iter = 0; iter < 42; iter++) {
    const mid = (lo + hi) / 2;
    const len = arcLengthOnEllipse(a, b, ELLIPSE_T0, mid, 72);
    if (len < targetFromT0) lo = mid;
    else hi = mid;
  }
  return (lo + hi) / 2;
}

/** Half-width / half-height of the axis-aligned bounds of a w×h rect rotated by `deg`. */
function rotatedAabbHalfExtents(w: number, h: number, deg: number) {
  const r = (deg * Math.PI) / 180;
  const c = Math.abs(Math.cos(r));
  const s = Math.abs(Math.sin(r));
  return { hw: (w * c + h * s) / 2, hh: (w * s + h * c) / 2 };
}

/** True if axis-aligned bounds of two rotated tiles overlap (no minimum gap). */
function rotatedTileAABBsOverlap(
  ax: number,
  ay: number,
  bx: number,
  by: number,
  box: BoxDim,
  degA: number,
  degB: number,
  gapPx: number
): boolean {
  const ha = rotatedAabbHalfExtents(box.w, box.h, degA);
  const hb = rotatedAabbHalfExtents(box.w, box.h, degB);
  const x0lo = ax - ha.hw;
  const x0hi = ax + ha.hw;
  const x1lo = bx - hb.hw;
  const x1hi = bx + hb.hw;
  const y0lo = ay - ha.hh;
  const y0hi = ay + ha.hh;
  const y1lo = by - hb.hh;
  const y1hi = by + hb.hh;
  const sepX = x0hi + gapPx <= x1lo || x1hi + gapPx <= x0lo;
  const sepY = y0hi + gapPx <= y1lo || y1hi + gapPx <= y0lo;
  return !(sepX || sepY);
}

/** Nudge every tile except the center (image 4) down; outer cards (1,2,6,7) get a bit more. */
function heroAlignNonCenterWithCenterDy(
  index: number,
  box: BoxDim,
  scale: number
): number {
  const mid = (N - 1) / 2;
  if (index === mid) return 0;
  const base = Math.round((12 + box.h * 0.026) * scale);
  const isOuter = index <= 1 || index >= N - 2;
  const extraOuter = isOuter
    ? Math.round((14 + box.h * 0.026) * scale)
    : 0;
  return base + extraOuter;
}

function adjacentHeroTilesOverlap(
  ovalTopLeft: { x: number; y: number }[],
  box: BoxDim,
  gapPx: number
): boolean {
  for (let i = 0; i < ovalTopLeft.length - 1; i++) {
    const p = ovalTopLeft[i]!;
    const q = ovalTopLeft[i + 1]!;
    const ax = p.x + box.w / 2;
    const ay = p.y + box.h / 2;
    const bx = q.x + box.w / 2;
    const by = q.y + box.h / 2;
    if (
      rotatedTileAABBsOverlap(
        ax,
        ay,
        bx,
        by,
        box,
        heroTileRotationDeg(i),
        heroTileRotationDeg(i + 1),
        gapPx
      )
    ) {
      return true;
    }
  }
  return false;
}

/** Scale tile center x toward 0 (arc axis) so the row packs tighter horizontally. */
function packCentersTowardArcAxisX(
  pts: { x: number; y: number }[],
  box: BoxDim,
  pack: number
): { x: number; y: number }[] {
  return pts.map((p) => {
    const cx = p.x + box.w / 2;
    const ncx = cx * pack;
    return { x: ncx - box.w / 2, y: p.y };
  });
}

/**
 * Minimum horizontal pack factor in [pack, 1] with no neighbor overlap.
 * Ellipse-only `a` can leave large screen gaps when the AABB test clears via y-separation.
 */
function tightestHorizontalPack(
  pts: { x: number; y: number }[],
  box: BoxDim,
  gapPx: number
): { x: number; y: number }[] {
  const packed = (pack: number) =>
    packCentersTowardArcAxisX(pts, box, pack);
  if (adjacentHeroTilesOverlap(pts, box, gapPx)) {
    return pts;
  }
  let lo = 0.42;
  let hi = 1;
  if (!adjacentHeroTilesOverlap(packed(lo), box, gapPx)) {
    hi = lo;
    lo = 0.22;
    let guard = 0;
    while (
      !adjacentHeroTilesOverlap(packed(lo), box, gapPx) &&
      lo > 0.12 &&
      guard++ < 45
    ) {
      hi = lo;
      lo -= 0.035;
    }
    if (!adjacentHeroTilesOverlap(packed(lo), box, gapPx)) {
      return packed(hi);
    }
  }
  for (let i = 0; i < 38; i++) {
    const mid = (lo + hi) / 2;
    if (adjacentHeroTilesOverlap(packed(mid), box, gapPx)) {
      lo = mid;
    } else {
      hi = mid;
    }
  }
  return packed(hi);
}

/**
 * Tile *centers* on an elliptic arc (π → 2π), equal spacing along arc length.
 * Top-left for each tile is returned.
 *
 * `a` starts small and grows until neighbors clear `gapPx`, then we **shrink** `a` with a
 * binary search to the smallest value that still clears — avoids the “wide arc” left by
 * multiplicative growth steps.
 */
type OvalCurveOptions = { bCurveMult?: number };

function ovalPointsAndMeta(
  scale: number,
  box: BoxDim,
  curve: OvalCurveOptions = {}
): { oval: { x: number; y: number }[]; a: number; b: number; pack: number } {
  const b =
    (64 + box.h * 0.12 + box.gap * 0.2) * scale * (curve.bCurveMult ?? 1);
  /** Tiny intentional gap (px); image-2–style closeness without overlap. */
  const gapPx = Math.max(0, Math.min(1, Math.round(box.gap * 0.006 * scale)));

  const build = (aVal: number) => {
    const totalLen = arcLengthOnEllipse(aVal, b, ELLIPSE_T0, ELLIPSE_T1, 160);
    const mid = (N - 1) / 2;
    const ts: number[] = new Array(N);
    ts[0] = ELLIPSE_T0;
    ts[mid] = 1.5 * Math.PI;
    ts[N - 1] = ELLIPSE_T1;
    for (let i = 1; i < mid; i++) {
      ts[i] = tForAccumulatedArcLength(
        aVal,
        b,
        (i / (N - 1)) * totalLen,
        totalLen
      );
    }
    for (let i = mid + 1; i < N - 1; i++) {
      const mirrorIdx = N - 1 - i;
      ts[i] = 3 * Math.PI - ts[mirrorIdx]!;
    }
    return ts.map((t, idx) => {
      const cx = aVal * Math.cos(t);
      const cy = b * Math.sin(t);
      const dy = heroAlignNonCenterWithCenterDy(idx, box, scale);
      return { x: cx - box.w / 2, y: cy - box.h / 2 + dy };
    });
  };

  const aSeed = Math.max(100, box.w * 0.38 + box.gap * 0.12) * scale;
  let a = aSeed;
  let pts = build(a);
  let attempts = 0;
  while (adjacentHeroTilesOverlap(pts, box, gapPx) && attempts < 160) {
    a *= 1.022;
    pts = build(a);
    attempts++;
  }
  while (adjacentHeroTilesOverlap(pts, box, gapPx) && attempts < 220) {
    a *= 1.04;
    pts = build(a);
    attempts++;
  }

  const overlapsAt = (aVal: number) =>
    adjacentHeroTilesOverlap(build(aVal), box, gapPx);

  let hi = a;
  if (overlapsAt(hi)) {
    return { oval: pts, a: hi, b, pack: 1 };
  }

  let lo = aSeed;
  if (!overlapsAt(lo)) {
    hi = aSeed;
    lo = aSeed * 0.22;
    let guard = 0;
    while (!overlapsAt(lo) && lo > 1 && guard++ < 52) {
      hi = lo;
      lo *= 0.93;
    }
    if (!overlapsAt(lo)) {
      const unpacked = build(hi);
      const packed = tightestHorizontalPack(unpacked, box, gapPx);
      const c0u = unpacked[0]!.x + box.w / 2;
      const c0p = packed[0]!.x + box.w / 2;
      const pack = c0u !== 0 ? c0p / c0u : 1;
      return { oval: packed, a: hi, b, pack };
    }
  }

  for (let i = 0; i < 44; i++) {
    const mid = (lo + hi) / 2;
    if (overlapsAt(mid)) {
      lo = mid;
    } else {
      hi = mid;
    }
  }
  const unpacked = build(hi);
  let ptsOut = tightestHorizontalPack(unpacked, box, gapPx);
  const c0u = unpacked[0]!.x + box.w / 2;
  const c0p = ptsOut[0]!.x + box.w / 2;
  const pack = c0u !== 0 ? c0p / c0u : 1;
  return { oval: ptsOut, a: hi, b, pack };
}

function ovalPoints(scale: number, box: BoxDim): { x: number; y: number }[] {
  return ovalPointsAndMeta(scale, box).oval;
}

/** Strip bounds from unrotated tile rects so left/right pairs stay vertically aligned (rotated AABB min/max skews y). */
function ovalLayoutMetrics(oval: { x: number; y: number }[], box: BoxDim) {
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  for (const p of oval) {
    minX = Math.min(minX, p.x);
    maxX = Math.max(maxX, p.x + box.w);
    minY = Math.min(minY, p.y);
    maxY = Math.max(maxY, p.y + box.h);
  }
  const spanX = maxX - minX;
  const spanY = maxY - minY;
  return { minX, maxX, minY, maxY, spanX, spanY };
}

/** Same elliptical arc geometry as the desktop hero token strip (for mobile marquee). */
export type HeroMarqueeBoxDim = { w: number; h: number; gap: number };

export function getHeroMarqueeOvalLayout(
  box: HeroMarqueeBoxDim,
  scale = 1
): {
  oval: { x: number; y: number }[];
  minX: number;
  minY: number;
  spanX: number;
  spanY: number;
  stripH: number;
  ovalYBase: number;
  boxLiftY: number;
} {
  const oval = ovalPoints(scale, box);
  const { minX, minY, spanX, spanY } = ovalLayoutMetrics(oval, box);
  const stripH = Math.max(spanY, box.h + 24);
  const ovalYBase = box.h * 0.18;
  const boxLiftY = stripH * 0.2;
  return { oval, minX, minY, spanX, spanY, stripH, ovalYBase, boxLiftY };
}

/** Ellipse + pack used to slide tokens along the same bottom arc (mobile endless wheel). */
export function getHeroMarqueeWheelGeometry(
  box: HeroMarqueeBoxDim,
  scale = 1,
  curve: OvalCurveOptions = {}
): {
  a: number;
  b: number;
  pack: number;
  minX: number;
  minY: number;
  spanX: number;
  spanY: number;
  stripH: number;
  ovalYBase: number;
  boxLiftY: number;
  totalArcLen: number;
} {
  const { oval, a, b, pack } = ovalPointsAndMeta(scale, box, curve);
  const { minX, minY, spanX, spanY } = ovalLayoutMetrics(oval, box);
  const stripH = Math.max(spanY, box.h + 24);
  const ovalYBase = box.h * 0.18;
  const boxLiftY = stripH * 0.2;
  const totalArcLen = arcLengthOnEllipse(a, b, ELLIPSE_T0, ELLIPSE_T1, 160);
  return {
    a,
    b,
    pack,
    minX,
    minY,
    spanX,
    spanY,
    stripH,
    ovalYBase,
    boxLiftY,
    totalArcLen,
  };
}

/** Deepens the bottom ellipse arc on mobile (higher `b` → more vertical bow). */
export const HERO_MOBILE_ARC_CURVE_MULT = 1.35;

/** `s` in [0,1): position along bottom ellipse (π→2π), right-to-left belt when driven with decreasing s. */
export function sampleHeroMarqueeAlongArc(
  geo: ReturnType<typeof getHeroMarqueeWheelGeometry>,
  box: HeroMarqueeBoxDim,
  s: number
): { left: number; top: number; tangentDeg: number } {
  const u = ((s % 1) + 1) % 1;
  const t = tForAccumulatedArcLength(
    geo.a,
    geo.b,
    u * geo.totalArcLen,
    geo.totalArcLen
  );
  const cx = geo.a * Math.cos(t);
  const cy = geo.b * Math.sin(t);
  const cxPacked = cx * geo.pack;
  const xTl = cxPacked - box.w / 2;
  const yTl = cy - box.h / 2;
  const left = xTl - geo.minX;
  const top = yTl - geo.minY + geo.ovalYBase - geo.boxLiftY;
  const rdx = -geo.a * Math.sin(t);
  const rdy = geo.b * Math.cos(t);
  const tangentDeg = (Math.atan2(rdy, rdx) * 180) / Math.PI;
  return { left, top, tangentDeg };
}

/** Stage delays (ms). */
export const HERO_STAGE_TIMING = {
  beforeStack: Math.round(80 * HERO_SPEED_MULT),
  stack: Math.round(800 * HERO_SPEED_MULT),
  /** After spread *starts*; much shorter than waiting for tiles to finish. */
  humansAfterSpreadStart: Math.round(200 * HERO_SPEED_MULT),
  /** Mobile: start endless marquee after spread has mostly settled. */
  marqueeAfterSpread: Math.round(750 * HERO_SPEED_MULT),
} as const;

/** Arc position [0, 1) for marquee tile `index` along the bottom ellipse. */
export function heroMarqueeArcFraction(index: number): number {
  return index / N;
}

function StaticOvalRow({
  oval,
  minX,
  minY,
  box,
  ovalYBase,
  boxLiftY,
  spanX,
  spanY,
}: {
  oval: { x: number; y: number }[];
  minX: number;
  minY: number;
  box: BoxDim;
  ovalYBase: number;
  boxLiftY: number;
  spanX: number;
  spanY: number;
}) {
  const h = Math.max(box.h + 32, spanY + 16);
  return (
    <div className="relative w-full min-w-0 overflow-x-visible overflow-y-visible">
      <div
        className="absolute overflow-visible"
        style={heroTileStripInnerStyle(spanX, h)}
      >
        {HERO_BOX_IMAGES.map((src, j) => (
          <div
            key={src + j}
            className="absolute origin-center overflow-hidden rounded-10 shadow-sm"
            style={{
              left: (oval[j]?.x ?? 0) - minX,
              top: (oval[j]?.y ?? 0) - minY + ovalYBase - boxLiftY,
              width: box.w,
              height: box.h,
              transform: `rotate(${heroTileRotationDeg(j)}deg)`,
            }}
          >
            <HeroTokenCardFace src={src} size={box.w} />
          </div>
        ))}
      </div>
    </div>
  );
}

/* ───────────── Endless conveyor along the fan (desktop) ───────────── */

/** Seconds a card takes to travel one slot of the fan (slow, ambient drift). */
const LOOP_SECONDS_PER_SLOT = 14;
/** Never add more than this many extra cards on each side of the seven. */
const MAX_EXTRA_SLOTS = 9;

type SlotPose = { left: number; top: number; rot: number };

const round2 = (n: number): number => Math.round(n * 100) / 100;

function catmullRom(p0: number, p1: number, p2: number, p3: number, t: number): number {
  return (
    0.5 *
    (2 * p1 +
      (-p0 + p2) * t +
      (2 * p0 - 5 * p1 + 4 * p2 - p3) * t * t +
      (-p0 + 3 * p1 - 3 * p2 + p3) * t * t * t)
  );
}

/**
 * Pose of a card at fractional slot `u`. Slots 0..N-1 are the seven fan positions from the design;
 * between them the path is a smooth Catmull-Rom curve, and beyond the ends it continues in a straight
 * line (position and the ±4° rotation step), so extra cards can ride in from off-screen.
 */
function poseAtSlot(table: SlotPose[], u: number): SlotPose {
  const n = table.length;
  if (u <= 0) {
    const a = table[0]!;
    const b = table[1]!;
    return {
      left: a.left + (b.left - a.left) * u,
      top: a.top + (b.top - a.top) * u,
      rot: a.rot + (b.rot - a.rot) * u,
    };
  }
  if (u >= n - 1) {
    const a = table[n - 2]!;
    const b = table[n - 1]!;
    const d = u - (n - 1);
    return {
      left: b.left + (b.left - a.left) * d,
      top: b.top + (b.top - a.top) * d,
      rot: b.rot + (b.rot - a.rot) * d,
    };
  }
  const i = Math.floor(u);
  const t = u - i;
  const p0 = table[Math.max(0, i - 1)]!;
  const p1 = table[i]!;
  const p2 = table[i + 1]!;
  const p3 = table[Math.min(n - 1, i + 2)]!;
  return {
    left: catmullRom(p0.left, p1.left, p2.left, p3.left, t),
    top: catmullRom(p0.top, p1.top, p2.top, p3.top, t),
    rot: p1.rot + (p2.rot - p1.rot) * t,
  };
}

type HeroLoopTileProps = {
  base: number;
  imageIndex: number;
  isExtra: boolean;
  table: SlotPose[];
  box: BoxDim;
  stage: Stage;
  phase: ReturnType<typeof useMotionValue<number>>;
  slotMin: number;
  slotCount: number;
  stackLeft: number;
  stackTop: number;
  enterTop: number;
  zIndex: number;
};

function HeroLoopTile({
  base,
  imageIndex,
  isExtra,
  table,
  box,
  stage,
  phase,
  slotMin,
  slotCount,
  stackLeft,
  stackTop,
  enterTop,
  zIndex,
}: HeroLoopTileProps) {
  const src = HERO_BOX_IMAGES[imageIndex]!;
  const rest = poseAtSlot(table, base);
  const stackX = stackLeft - rest.left;
  const stackY = stackTop - rest.top;
  const enterY = enterTop - rest.top;

  const offsetX = useMotionValue(stackX);
  const offsetY = useMotionValue(enterY);
  const rotOffset = useMotionValue(-rest.rot);
  const opacity = useMotionValue(0);

  useEffect(() => {
    // The seven design cards stack, then spread; the extra cards only fade in once the loop starts.
    const delayStack = base * HERO_CARD_STAGGER_STACK_SEC;
    const delaySpread = base * HERO_CARD_STAGGER_SPREAD_SEC;
    if (stage === 0) {
      offsetX.set(stackX);
      offsetY.set(enterY);
      rotOffset.set(-rest.rot);
      opacity.set(0);
      return;
    }
    if (isExtra) {
      offsetX.set(0);
      offsetY.set(0);
      rotOffset.set(0);
      if (stage === 3) void animate(opacity, 1, { duration: 0.9, ease: 'easeOut' });
      return;
    }
    if (stage === 1) {
      void animate(offsetX, stackX, { ...HERO_CARD_SPRING_STACK, delay: delayStack });
      void animate(offsetY, stackY, { ...HERO_CARD_SPRING_STACK, delay: delayStack });
      void animate(rotOffset, -rest.rot, { ...HERO_CARD_SPRING_STACK, delay: delayStack });
      void animate(opacity, 1, { duration: 0, delay: delayStack });
    } else if (stage === 2) {
      void animate(offsetX, 0, { ...HERO_CARD_SPRING_SPREAD, delay: delaySpread });
      void animate(offsetY, 0, { ...HERO_CARD_SPRING_SPREAD, delay: delaySpread });
      void animate(rotOffset, 0, { ...HERO_CARD_SPRING_SPREAD, delay: delaySpread });
    }
  }, [stage, base, isExtra, stackX, stackY, enterY, rest.rot, offsetX, offsetY, rotOffset, opacity]);

  const transform = useTransform([phase, offsetX, offsetY, rotOffset], ([p, ox, oy, ro]) => {
    // Cards travel right → left; wrap inside [slotMin, slotMin + slotCount) (always off-screen).
    const raw = base - (p as number);
    const slot = ((((raw - slotMin) % slotCount) + slotCount) % slotCount) + slotMin;
    const pose = poseAtSlot(table, slot);
    return `translate3d(${round2(pose.left + (ox as number))}px, ${round2(pose.top + (oy as number))}px, 0) rotate(${round2(pose.rot + (ro as number))}deg)`;
  });

  return (
    <motion.div
      className="absolute left-0 top-0 origin-center overflow-hidden rounded-10 shadow-sm transform-gpu [backface-visibility:hidden]"
      style={{ width: box.w, height: box.h, zIndex, transform, opacity }}
    >
      <HeroTokenCardFace src={src} size={box.w} priority={!isExtra} />
    </motion.div>
  );
}

function useWindowWidth(): number {
  return useSyncExternalStore(
    (onChange) => {
      window.addEventListener('resize', onChange);
      return () => window.removeEventListener('resize', onChange);
    },
    () => window.innerWidth,
    () => 1440
  );
}

type HeroAnimatedStageProps = {
  onHumansReveal?: () => void;
  /** Scale of the 1440px design frame the stage sits in (used to know how wide the window is in frame units). */
  frameScale?: number;
};

export function HeroAnimatedStage({ onHumansReveal, frameScale = 1 }: HeroAnimatedStageProps) {
  const shouldReduce = Boolean(useReducedMotion());
  const { scale, box } = useHeroVisualConfig();
  const [stage, setStage] = useState<Stage>(0);
  const hasNotifiedRef = useRef(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const phase = useMotionValue(0);
  const windowWidth = useWindowWidth();
  const oval = useMemo(
    () => ovalPoints(scale, box),
    [scale, box.w, box.h, box.gap]
  );
  const { spanX, spanY, minX, minY } = useMemo(
    () => ovalLayoutMetrics(oval, box),
    [oval, box.w, box.h, box.gap]
  );
  const ovalYBase = box.h * 0.18;
  const stackLeft = (spanX - box.w) / 2;
  const stripH = useMemo(
    () => Math.max(spanY, box.h + 24),
    [spanY, box.h]
  );
  /** Move the whole arc up (20% of strip height). */
  const boxLiftY = stripH * 0.2;
  const enterTop = spanY + box.h * 0.8 - boxLiftY;

  /** The seven fan positions from the design, as top-left + rotation, indexed by slot. */
  const slotTable = useMemo<SlotPose[]>(
    () =>
      oval.map((p, i) => ({
        left: p.x - minX,
        top: p.y - minY + ovalYBase - boxLiftY,
        rot: heroTileRotationDeg(i),
      })),
    [oval, minX, minY, ovalYBase, boxLiftY]
  );
  const stackTop = (slotTable[3]?.top ?? 0) + box.h * 0.12;

  /**
   * Extra cards on each side so the point where a card leaves one end and re-enters at the other is
   * always outside the window, whatever its width.
   */
  const extraSlots = useMemo(() => {
    const need = windowWidth / (2 * frameScale) + box.w * 0.6;
    const centerX = (u: number) =>
      Math.abs(poseAtSlot(slotTable, u).left + box.w / 2 - spanX / 2);
    for (let k = 1; k < MAX_EXTRA_SLOTS; k++) {
      if (centerX(-k - 0.5) > need && centerX(N - 1 + k + 0.5) > need) return k;
    }
    return MAX_EXTRA_SLOTS;
  }, [slotTable, windowWidth, frameScale, box.w, spanX]);
  const slotCount = N + extraSlots * 2;
  const slotMin = -extraSlots - 0.5;

  useEffect(() => {
    if (hasNotifiedRef.current) return;
    if (shouldReduce || stage >= 3) {
      hasNotifiedRef.current = true;
      onHumansReveal?.();
    }
  }, [onHumansReveal, shouldReduce, stage]);

  useEffect(() => {
    if (shouldReduce) {
      setStage(3);
      return;
    }
    const t = setTimeout(() => setStage(1), HERO_STAGE_TIMING.beforeStack);
    return () => clearTimeout(t);
  }, [shouldReduce]);

  useEffect(() => {
    if (shouldReduce || stage !== 1) return;
    const t = setTimeout(() => setStage(2), HERO_STAGE_TIMING.stack);
    return () => clearTimeout(t);
  }, [stage, shouldReduce]);

  useEffect(() => {
    if (shouldReduce || stage !== 2) return;
    const t = setTimeout(() => setStage(3), HERO_STAGE_TIMING.humansAfterSpreadStart);
    return () => clearTimeout(t);
  }, [stage, shouldReduce]);

  // Endless slow drift once the intro has settled. Paused while the hero is off-screen.
  useEffect(() => {
    if (shouldReduce || stage < 3) return;
    // Advance by elapsed time (not a restarting tween) so a resize never makes the cards jump.
    const tick = (_time: number, deltaMs: number) => {
      phase.set((phase.get() + deltaMs / 1000 / LOOP_SECONDS_PER_SLOT) % slotCount);
    };
    let running = false;
    const setRunning = (on: boolean) => {
      if (on === running) return;
      running = on;
      if (on) gsap.ticker.add(tick);
      else gsap.ticker.remove(tick);
    };
    const root = rootRef.current;
    const observer = new IntersectionObserver(([entry]) => setRunning(Boolean(entry?.isIntersecting)));
    if (root) observer.observe(root);
    else setRunning(true);
    return () => {
      observer.disconnect();
      setRunning(false);
    };
  }, [shouldReduce, stage, slotCount, phase]);

  return (
    <div
      ref={rootRef}
      className="relative isolate h-[340px] w-full overflow-x-clip overflow-y-visible sm:h-[460px] sm:overflow-visible md:h-[680px]"
      aria-label="Proply hero illustration"
    >
      {/*
        Tiles first in DOM; same hero bounds as the figures (inset-0) so the arc
        can sit *behind* the characters (z-10 vs z-20) instead of in a separate row below.
        The strip is centered (left 50% + -translate-x-1/2, width: spanX). Tiles may
        extend past the viewport; overflow is visible.
      */}
      <div className="absolute inset-0 z-10 pointer-events-none">
        {shouldReduce && (
          <div className="absolute inset-x-0 bottom-0 top-0 flex max-h-full items-end justify-center overflow-x-visible pb-2 sm:pb-3">
            <div className="flex w-full min-w-0 justify-center overflow-x-visible">
              <StaticOvalRow
                oval={oval}
                minX={minX}
                minY={minY}
                box={box}
                ovalYBase={ovalYBase}
                boxLiftY={boxLiftY}
                spanX={spanX}
                spanY={spanY}
              />
            </div>
          </div>
        )}

        {!shouldReduce && (
          <div
            className="absolute overflow-visible pb-2 sm:pb-3"
            style={heroTileStripInnerStyle(spanX, stripH)}
          >
            {Array.from({ length: slotCount }, (_, j) => {
              const base = j - extraSlots;
              const imageIndex = ((base % N) + N) % N;
              return (
                <HeroLoopTile
                  key={base}
                  base={base}
                  imageIndex={imageIndex}
                  isExtra={base < 0 || base > N - 1}
                  table={slotTable}
                  box={box}
                  stage={stage}
                  phase={phase}
                  slotMin={slotMin}
                  slotCount={slotCount}
                  stackLeft={stackLeft}
                  stackTop={stackTop}
                  enterTop={enterTop}
                  zIndex={5 + j}
                />
              );
            })}
          </div>
        )}
      </div>

      <motion.div
        className="absolute inset-0 z-20 flex items-center justify-center pointer-events-none transform-gpu [backface-visibility:hidden] md:pb-[82px]"
        initial={false}
        animate={
          shouldReduce
            ? { opacity: 1 }
            : stage >= 3
              ? { opacity: 1 }
              : { opacity: 0 }
        }
        transition={HERO_HUMAN_FADE_IN}
        aria-hidden={!shouldReduce && stage < 3}
      >
        <Image
          src="/images/img_container.svg"
          alt="People exploring property listings on their phones"
          width={HUMAN_W}
          height={HUMAN_H}
          className="h-[200px] w-[256px] object-contain sm:h-[422px] sm:w-[540px] md:h-[564px] md:w-[min(92vw,760px)]"
          priority
        />
      </motion.div>
    </div>
  );
}
